import React, { useMemo } from 'react';
import classNames from 'classnames';
import VerifiedBadge from 'components/VerifiedBadge';
import { User } from 'models/User';
import { useTranslation } from 'react-i18next';
import PhoneIcon from 'icons/Phone.icon';
import MailIcon from 'icons/Mail.icon';
import DuplicateIcon from 'icons/Duplicate.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import { ReactComponent as LinkedInIcon } from 'icons/linkedin.svg';
import { Button } from 'ncoded-component-library';
import utils, { getIsUserVerified, getUserFullName } from 'utils';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { NEW_WINDOW_PROPS } from 'data/global';
import { EnquiryQuestionType } from 'router/subrouters/Dash/subrouters/Business/components/EnquirySlider/utils';
import ShowEnquiryAnswers from 'router/subrouters/Dash/subrouters/private/components/ShowEnquiryAnswers';
import useCopyToClipboard from 'hooks/useCopyToClipboard';

import './BuyerInfo.styles.scss';

type BuyerInfoProps = {
  buyer: User;
  enquiryQuestions?: EnquiryQuestionType[];
  leadWithdrawnInterest?: boolean;
  className?: string;
  dealRoomOwnerName?: string;
  isExternalCommunication?: boolean;
};

const BuyerInfo: React.FC<BuyerInfoProps> = (props) => {
  const {
    className,
    buyer,
    enquiryQuestions,
    leadWithdrawnInterest,
    dealRoomOwnerName,
    isExternalCommunication = false,
  } = props;

  const {
    firstName,
    lastName,
    hasVerifiedEmail,
    email,
    hasVerifiedContactNumber,
    phone,
    hasVerifiedLinkedinAccount,
    linkedinAccount,
    accountant,
  } = buyer;

  const formattedPhone = formatPhoneNumber(phone);

  const { t } = useTranslation();

  const { linkedinUrl, vanityName } =
    utils.getLinkedinUrlAndVanityName(linkedinAccount);

  const VerifiedBuyer = useMemo(
    () =>
      getIsUserVerified(hasVerifiedEmail, hasVerifiedContactNumber) && (
        <VerifiedBadge text={t('verified')} />
      ),
    [hasVerifiedContactNumber, hasVerifiedEmail, t],
  );

  const VerifiedInvestor = useMemo(
    () =>
      accountant?.formStatus === 'completed' && (
        <VerifiedBadge text={t('investor')} />
      ),
    [accountant, t],
  );

  const copyToClipboard = useCopyToClipboard();

  const classes = classNames('buyer-info', className);

  return (
    <div className={classes}>
      <div className="buyer-info__title">
        {isExternalCommunication ? (
          <>
            <span>{t('thirdPartyConverstionBetween')}</span>
            <div>
              <p>{dealRoomOwnerName}</p>
            </div>
            <span>{t('andTheEnquirer')}</span>
          </>
        ) : (
          <span>{t('nowChattingWith')}</span>
        )}

        <div>
          <p>{getUserFullName(firstName, lastName)}</p>
          <div>
            {VerifiedBuyer}
            {VerifiedInvestor}
            {leadWithdrawnInterest && (
              <p className="withdrawn-tag">{t('withdrawn')}</p>
            )}
          </div>
        </div>
      </div>

      <div className="buyer-info__info">
        {email && (
          <div>
            <MailIcon />
            <p>{email}</p>
            <Button
              variant="icon"
              icon={DuplicateIcon}
              onClick={(e) => copyToClipboard(e, email)}
            />
            {hasVerifiedEmail ? (
              <CheckcircleIcon className="verified" />
            ) : (
              <XcircleIcon className="not-verified" />
            )}
          </div>
        )}
        {formattedPhone && (
          <div>
            <PhoneIcon />
            <p>{formattedPhone}</p>
            <Button
              variant="icon"
              icon={DuplicateIcon}
              onClick={(e) => copyToClipboard(e, formattedPhone)}
            />
            {hasVerifiedContactNumber ? (
              <CheckcircleIcon className="verified" />
            ) : (
              <XcircleIcon className="not-verified" />
            )}
          </div>
        )}
        {linkedinAccount && (
          <div>
            <LinkedInIcon />
            <p>{vanityName}</p>
            <a href={linkedinUrl} {...NEW_WINDOW_PROPS}>
              <LinkToIcon />
            </a>
            {hasVerifiedLinkedinAccount ? (
              <CheckcircleIcon className="verified" />
            ) : (
              <XcircleIcon className="not-verified" />
            )}
          </div>
        )}
      </div>

      <ShowEnquiryAnswers enquiryQuestions={enquiryQuestions} />
    </div>
  );
};

export default BuyerInfo;
